import { useState, useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { blogApiBase } from "./config";
import { track } from "./analytics/track";
import { useScrollDepth } from "./analytics/useScrollDepth";
import { rememberArticleReferrer } from "./analytics/attribution";
import {
  type Blog,
  BlogImage,
  BlogMeta,
  formatDate,
  readingTime,
} from "./blogsShared";

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Blog | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const articleRef = useRef<HTMLElement>(null);

  useScrollDepth(post ? slug : undefined, articleRef);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    let cancelled = false;

    const loadPost = async () => {
      setIsLoading(true);
      setIsError(false);
      try {
        const res = await fetch(
          `${blogApiBase()}/clients/new_dawn/blog/${slug}`
        );
        if (!res.ok) {
          throw new Error(`Failed to load blog: ${res.status}`);
        }
        const data: Blog = await res.json();
        if (!cancelled) {
          setPost(data);
          // Only a successful load is a real read — a 404 would inflate the
          // conversion-rate denominator.
          if (slug) track("article-view", { slug });
        }
      } catch (error) {
        console.error("Error loading blog:", error);
        if (!cancelled) setIsError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadPost();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <main className="min-h-screen bg-[#f9fafb] px-6 pb-24 pt-24 md:px-10 md:pt-32">
      <div className="mx-auto w-full max-w-2xl">
        <Link
          to="/#blogs"
          className="font-nunito font-bold text-sm text-ink-soft transition-colors hover:text-sage-deep"
        >
          ← Back to blogs
        </Link>

        {isLoading && (
          <p className="mt-12 text-sm font-trirong_light text-meta">
            Loading blog…
          </p>
        )}

        {!isLoading && (isError || !post) && (
          <div className="mt-12 space-y-4">
            <p className="text-sm font-trirong_light text-meta">
              This blog could not be found.
            </p>
          </div>
        )}

        {!isLoading && !isError && post && (
          <article ref={articleRef}>
            <BlogMeta
              parts={[formatDate(post.created_at), readingTime(post.body_html)]}
              className="mt-7"
            />
            <h1 className="mt-4 text-4xl md:text-5xl font-trirong_light leading-tight text-ink">
              {post.title.replace(/…$/, "")}
            </h1>

            <BlogImage
              post={post}
              className="mt-9 h-60 w-full rounded-2xl md:h-[380px]"
            />

            <div
              className="prose prose-lg mt-10 max-w-none font-trirong_light text-ink
                         prose-headings:font-trirong_reg prose-headings:text-ink
                         prose-p:font-trirong_light prose-p:text-ink prose-p:leading-relaxed
                         prose-blockquote:border-l-sage prose-a:text-lime-700"
              dangerouslySetInnerHTML={{ __html: post.body_html }}
            />

            <div className="mt-12 border-t border-hairline pt-8 text-center">
              <p className="font-nunito text-sm text-ink-soft">
                If something here resonated, I would be glad to hear from you.
              </p>
              <Link
                to="/#contact"
                onClick={() => {
                  if (!slug) return;
                  track("article-cta-click", { slug });
                  rememberArticleReferrer(slug);
                }}
                className="mt-5 inline-block rounded-full bg-sage px-9 py-3.5 font-nunito font-bold text-sm text-white transition-colors hover:bg-sage-deep"
              >
                Get in touch
              </Link>
            </div>
          </article>
        )}
      </div>
    </main>
  );
}
